import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button, Input, Spin, Typography, message, type InputRef } from 'antd'
import { CloseOutlined, SendOutlined, StopOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { adminApi } from '../api'
import {
  failureFromErrorEvent,
  failureFromThrownError,
  stripWritingFence,
  streamDocWriting,
  WRITING_ERROR_SOURCE_LABEL,
  type DocWritingErrorSource,
  type DocWritingFailure,
  type DocWritingFormat,
  type DocWritingMessage,
} from '../api/docWritingStream'
import { buildCursorContextExcerpt, buildCursorModeUserHint } from '../lib/editorCursorAnchor'
import './DocInlineWritingPanel.css'

const { Text } = Typography

type InlineMessage = DocWritingMessage & {
  isFailure?: boolean
  failureSource?: DocWritingErrorSource
  failureDetail?: string
}

function failureInlineMessage(failure: DocWritingFailure): InlineMessage {
  return {
    role: 'assistant',
    content: failure.message,
    failureDetail: failure.detail,
    isFailure: true,
    failureSource: failure.source,
  }
}

const SELECTION_INTENT_HINTS = ['扩写选中内容', '精简这段话']

/** // 光标处无选区：续写列表/段落 */
const CURSOR_INTENT_HINTS = ['补全当前条目', '继续扩写内容', '补充案例细节', '写承上启下段']

export type DocInlineWritingMode = 'cursor' | 'selection'

export type DocInlineInsertMode = 'cursor' | 'replace' | 'afterSelection'

type Props = {
  open: boolean
  mode: DocInlineWritingMode
  onClose: () => void
  format: DocWritingFormat
  title: string
  category: string
  getAnchorRect: () => DOMRect | null
  scrollRootRef: React.RefObject<HTMLElement | null>
  getContextBeforeCursor: () => string
  getContextAfterCursor: () => string
  getDocumentContent: () => string
  selection?: string
  selectionPlain?: string
  onInsert: (content: string, mode: DocInlineInsertMode) => void
}

function clampPanelPos(top: number, left: number, panelW: number, panelH: number) {
  const margin = 8
  let nextLeft = left
  let nextTop = top
  if (nextLeft + panelW > window.innerWidth - margin) {
    nextLeft = Math.max(margin, window.innerWidth - panelW - margin)
  }
  nextLeft = Math.max(margin, nextLeft)
  if (nextTop + panelH > window.innerHeight - margin) {
    nextTop = Math.max(margin, window.innerHeight - panelH - margin)
  }
  nextTop = Math.max(margin, nextTop)
  return { top: nextTop, left: nextLeft }
}

export function DocInlineWritingPanel({
  open,
  mode,
  onClose,
  format,
  title,
  category,
  getAnchorRect,
  scrollRootRef,
  getContextBeforeCursor,
  getContextAfterCursor,
  getDocumentContent,
  selection = '',
  selectionPlain = '',
  onInsert,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<InputRef>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const turnMessagesRef = useRef<InlineMessage[]>([])

  const [ready, setReady] = useState<boolean | null>(null)
  const [messages, setMessages] = useState<InlineMessage[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [streamText, setStreamText] = useState('')
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const repositionFromAnchor = useCallback(() => {
    const rect = getAnchorRect()
    if (!rect) return
    const panel = panelRef.current
    const w = panel?.offsetWidth ?? 360
    const h = panel?.offsetHeight ?? 240
    setPos(clampPanelPos(rect.bottom + 8, rect.left, w, h))
  }, [getAnchorRect])

  const isSelectionMode = mode === 'selection'
  const pinnedSelection = (selectionPlain || selection).trim()
  const pinnedSelectionContent = selection.trim()

  const welcomeGreeting = useMemo(() => {
    if (isSelectionMode && pinnedSelection) {
      return `已选中 ${pinnedSelection.length} 字。请描述你想对选中内容做什么，生成后可替换选中或插在其后。`
    }
    return '光标位置。已参考光标前后正文，请描述你想在这里补充或插入什么。'
  }, [isSelectionMode, pinnedSelection])

  useEffect(() => {
    adminApi
      .get<{ ready: boolean }>('/admin/knowledge/doc-writing/ready')
      .then((r) => setReady(r.data.ready))
      .catch(() => setReady(false))
  }, [])

  useEffect(() => {
    if (!open) {
      setMessages([])
      setInput('')
      setStreamText('')
      setStreaming(false)
      abortRef.current?.abort()
      abortRef.current = null
      return
    }
    repositionFromAnchor()
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => window.clearTimeout(timer)
  }, [open, repositionFromAnchor])

  useEffect(() => {
    if (!open) return
    repositionFromAnchor()
    const scrollRoot = scrollRootRef.current
    const onScroll = () => repositionFromAnchor()
    scrollRoot?.addEventListener('scroll', onScroll, true)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      scrollRoot?.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [open, repositionFromAnchor, scrollRootRef])

  useEffect(() => {
    if (!open) return
    repositionFromAnchor()
  }, [open, messages.length, streamText, repositionFromAnchor])

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, streamText])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onMouseDown = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node)) return
      onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onMouseDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [open, onClose])

  const send = useCallback(
    async (text?: string, opts?: { skipUserBubble?: boolean; userDisplay?: string }) => {
      const userMsg = (text ?? input).trim()
      if (!userMsg || streaming) return
      const userBubble = (opts?.userDisplay ?? userMsg).trim() || userMsg
      const historySnapshot = messages
      const nextHistory: InlineMessage[] = opts?.skipUserBubble
        ? historySnapshot
        : [...historySnapshot, { role: 'user', content: userBubble }]
      turnMessagesRef.current = nextHistory

      if (!ready) {
        const failure = failureInlineMessage({
          source: 'config',
          message: '写作助手未就绪，请先在「模型管理」中配置对话模型 API Key。',
        })
        setMessages([...nextHistory, failure])
        setInput('')
        return
      }

      if (!opts?.skipUserBubble) {
        setMessages(nextHistory)
      }
      setInput('')
      setStreaming(true)
      setStreamText('')

      let documentExcerpt = getDocumentContent().slice(0, 12000)
      let apiUserMsg = userMsg
      if (isSelectionMode && pinnedSelectionContent) {
        documentExcerpt = getDocumentContent().slice(0, 12000)
        apiUserMsg = `${userMsg}（请基于选中内容处理，输出可直接用于替换选中或插在其后）`
      } else {
        const beforeCursor = getContextBeforeCursor().trim()
        const afterCursor = getContextAfterCursor().trim()
        documentExcerpt = buildCursorContextExcerpt(beforeCursor, afterCursor)
        if (beforeCursor || afterCursor) {
          apiUserMsg = buildCursorModeUserHint(userMsg, beforeCursor)
        } else {
          apiUserMsg = `${userMsg}（请生成适合插入当前光标位置的内容）`
        }
      }

      const controller = new AbortController()
      abortRef.current = controller
      let acc = ''
      let gotReply = false

      const commitAssistantReply = (reply: InlineMessage) => {
        if (gotReply) return
        gotReply = true
        const next = [...turnMessagesRef.current, reply]
        turnMessagesRef.current = next
        setMessages(next)
        setStreaming(false)
        setStreamText('')
      }

      const commitFailure = (failure: DocWritingFailure) => {
        commitAssistantReply(failureInlineMessage(failure))
      }

      try {
        await streamDocWriting(
          {
            format,
            action: 'chat',
            user_message: apiUserMsg,
            title: title.trim(),
            category: category.trim(),
            selection: isSelectionMode && pinnedSelectionContent ? pinnedSelectionContent : undefined,
            document_excerpt: documentExcerpt,
            messages: historySnapshot.slice(-12),
          },
          (ev) => {
            if (ev.type === 'content') {
              acc += ev.text
              setStreamText(acc)
            } else if (ev.type === 'error') {
              commitFailure(failureFromErrorEvent(ev))
            } else if (ev.type === 'done') {
              const finalText = stripWritingFence((ev.content || acc).trim(), format)
              if (finalText) {
                commitAssistantReply({ role: 'assistant', content: finalText })
              } else {
                commitFailure({
                  source: 'llm_empty',
                  message: '服务端返回空的完成事件，未包含正文内容。',
                  detail: 'done.content 与流式 content 均为空',
                })
              }
            }
          },
          controller.signal,
        )
        if (!gotReply && acc.trim()) {
          commitAssistantReply({ role: 'assistant', content: stripWritingFence(acc.trim(), format) })
        } else if (!gotReply && !acc.trim()) {
          commitFailure({
            source: 'frontend',
            message: '前端未收到完整流式响应（连接中断或未收到 done/error 事件）。',
            detail: 'stream closed without done, error, or content',
          })
        }
      } catch (e) {
        if ((e as Error).name === 'AbortError') {
          if (acc.trim()) {
            commitAssistantReply({ role: 'assistant', content: stripWritingFence(acc.trim(), format) })
          } else {
            setStreaming(false)
            setStreamText('')
          }
        } else if (!gotReply) {
          commitFailure(failureFromThrownError(e))
        }
      } finally {
        setStreaming(false)
        abortRef.current = null
      }
    },
    [
      category,
      format,
      getContextBeforeCursor,
      getContextAfterCursor,
      getDocumentContent,
      input,
      isSelectionMode,
      messages,
      pinnedSelectionContent,
      ready,
      streaming,
      title,
    ],
  )

  const insertContent = (raw: string, insertMode: DocInlineInsertMode) => {
    const cleaned = stripWritingFence(raw.trim(), format)
    if (!cleaned) {
      message.warning('没有可插入的内容')
      return
    }
    onInsert(cleaned, insertMode)
    onClose()
  }

  const renderInsertActions = (content: string) => {
    if (isSelectionMode) {
      return (
        <div className="doc-inline-writing-msg-actions">
          <Button
            type="link"
            size="small"
            className="doc-inline-writing-msg-action doc-inline-writing-msg-action--primary"
            onClick={() => insertContent(content, 'replace')}
          >
            替换选中
          </Button>
          <Button
            type="text"
            size="small"
            className="doc-inline-writing-msg-action"
            onClick={() => insertContent(content, 'afterSelection')}
          >
            插入选区后
          </Button>
        </div>
      )
    }
    return (
      <div className="doc-inline-writing-msg-actions">
        <Button
          type="link"
          size="small"
          className="doc-inline-writing-msg-action doc-inline-writing-msg-action--primary"
          onClick={() => insertContent(content, 'cursor')}
        >
          插入光标处
        </Button>
      </div>
    )
  }

  if (!open) return null

  const showWelcome = messages.length === 0 && !streaming
  const showHints = !streaming
  const intentHints = isSelectionMode ? SELECTION_INTENT_HINTS : CURSOR_INTENT_HINTS

  return createPortal(
    <div
      ref={panelRef}
      className="doc-inline-writing-panel"
      style={{ top: pos.top, left: pos.left }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="doc-inline-writing-head">
        <span className="doc-inline-writing-head-title">
          <ThunderboltOutlined />
          描述意图
        </span>
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} aria-label="关闭" />
      </div>

      {isSelectionMode && pinnedSelection ? (
        <div className="doc-inline-writing-selection">
          <Text type="secondary">已选中 {pinnedSelection.length} 字</Text>
          <Text ellipsis className="doc-inline-writing-selection-preview">
            {pinnedSelection.slice(0, 160)}
          </Text>
        </div>
      ) : !isSelectionMode ? (
        <div className="doc-inline-writing-selection">
          <Text type="secondary">光标位置 · 已参考光标前后正文</Text>
        </div>
      ) : null}

      <div className="doc-inline-writing-messages" ref={listRef}>
        {showWelcome ? (
          <div className="doc-inline-writing-msg doc-inline-writing-msg--ai doc-inline-writing-msg--greeting">
            {welcomeGreeting}
          </div>
        ) : null}

        {messages.map((m, i) => (
          <div
            key={`${m.role}-${i}`}
            className={
              m.role === 'user'
                ? 'doc-inline-writing-msg doc-inline-writing-msg--user'
                : 'doc-inline-writing-msg doc-inline-writing-msg--ai'
            }
          >
            {m.isFailure ? (
              <>
                {m.failureSource ? (
                  <Text type="secondary" className="doc-inline-writing-reply-source">
                    {WRITING_ERROR_SOURCE_LABEL[m.failureSource]}
                  </Text>
                ) : null}
                <div>{m.content}</div>
                {m.failureDetail ? (
                  <Text type="secondary" className="doc-inline-writing-failure-detail">
                    {m.failureDetail}
                  </Text>
                ) : null}
              </>
            ) : (
              m.content
            )}
            {m.role === 'assistant' && !m.isFailure ? renderInsertActions(m.content) : null}
          </div>
        ))}

        {streaming ? (
          <div className="doc-inline-writing-streaming">
            <Spin size="small" />
            <Text type="secondary">{streamText ? '正在输出…' : '正在生成…'}</Text>
          </div>
        ) : null}
        {streaming && streamText ? (
          <div className="doc-inline-writing-msg doc-inline-writing-msg--ai">{streamText}</div>
        ) : null}
      </div>

      <div className="doc-inline-writing-input">
        {showHints ? (
          <div className="doc-inline-writing-hints">
            {intentHints.map((hint) => (
              <button
                key={hint}
                type="button"
                className="doc-inline-writing-hint"
                disabled={!ready}
                onClick={() => setInput(hint)}
              >
                {hint}
              </button>
            ))}
          </div>
        ) : null}
        <Input.TextArea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            isSelectionMode
              ? '描述你想对选中内容做什么…'
              : '描述你想在光标处写什么…'
          }
          autoSize={{ minRows: 2, maxRows: 4 }}
          disabled={streaming || !ready}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void send()
            }
          }}
        />
        <div className="doc-inline-writing-input-actions">
          {streaming ? (
            <Button
              icon={<StopOutlined />}
              onClick={() => {
                abortRef.current?.abort()
              }}
            >
              停止
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<SendOutlined />}
              disabled={!input.trim() || !ready}
              onClick={() => void send()}
            >
              发送
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
